// Explore-stats page: Party representation in each parliament.
// Recreates the Handbook page — bar of parliamentarians per party for a
// selected Parliament + line of the major parties' representation over
// time, with a chamber filter and Parliament selector.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, ToggleButton, ToggleButtonGroup,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatLine } from './charts';

export default function PartyRepresentation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chamber, setChamber] = useState('all');
  const [parliament, setParliament] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (chamber !== 'all') params.chamber = chamber;
        if (parliament) params.parliament = parliament;
        const res = await getExploreStat('party-representation', params);
        if (active) {
          setData(res);
          setError(null);
          if (!parliament && res.charts?.selected) {
            setParliament(res.charts.selected);
          }
        }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chamber, parliament]);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Party representation in each parliament"
      description="The number of parliamentarians in each party during a selected Parliament, and the rise and fall of the major parties over time."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="party-representation"
    >
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={chamber}
                           onChange={(e, v) => v && setChamber(v)}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="members">House</ToggleButton>
          <ToggleButton value="senators">Senate</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Parliament</InputLabel>
          <Select label="Parliament" value={parliament || ''}
                  onChange={(e) => setParliament(e.target.value)}>
            {(charts.parliaments || []).map((p) => (
              <MenuItem key={p} value={p}>{`Parliament ${p}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Party representation — Parliament {charts.selected}
            </Typography>
            <StatBar data={charts.selected_bar || []} xKey="party"
                     yKey="count" layout="vertical" height={380} />
          </Paper>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Party representation over time
            </Typography>
            <StatLine data={charts.over_time || []} xKey="parliament"
                      keys={charts.over_time_series || []} />
          </Paper>
        </>
      )}
    </ExploreStatsPage>
  );
}
